import { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { 
    Drawer, 
    Box, 
    Typography, 
    IconButton,
    Divider,
    Button,
    SelectChangeEvent,
} from '@mui/material';

import CloseIcon from '../../../../components/icons/CloseIcon';
import Row from '../../../../components/ui/core/Row';
import CoreSelect from '../../../../components/ui/core/CoreSelect';
import CoreInput from '../../../../components/ui/core/CoreInput';
import { useUploadHistoryStore } from '../../../../hooks/useUploadHistoryStore';
import { SelectOption } from '../../../../components/ui/core/CoreSelect';
import { formatLocalDate } from '../../../../stores/UploadHistoryStore';

interface AllFiltersDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

const STATUS_OPTIONS: SelectOption[] = [
    { value: '', label: 'All' },
    { value: 'PROCESSED', label: 'Processed' },
    { value: 'SKIPPED', label: 'Skipped' },
    { value: 'ERROR', label: 'Error' },
    { value: 'UPLOADED', label: 'Uploaded' },
    { value: 'PROCESSING', label: 'Processing' },
];

const EDI_TYPE_OPTIONS: SelectOption[] = [
    { value: '', label: 'All' },
    { value: 'CLAIM', label: 'Claim' },
    { value: 'PAYMENT', label: 'Payment' },
    { value: 'CLAIM_STATUS', label: 'Claim Status' },
];

const AllFiltersDrawer: FC<AllFiltersDrawerProps> = ({ isOpen, onClose }) => {
    const store = useUploadHistoryStore();
    
    const [localStatusFilter, setLocalStatusFilter] = useState<string>(store.statusFilter || '');
    const [localEdiTypeFilter, setLocalEdiTypeFilter] = useState<string>(store.ediTypeFilter || '');
    const [localFileNameFilter, setLocalFileNameFilter] = useState<string>(store.fileNameFilter || '');
    const [localClaimNumberFilter, setLocalClaimNumberFilter] = useState<string>(store.claimNumberFilter || '');
    const [localStartDate, setLocalStartDate] = useState<string>(
        formatLocalDate(store.dateRange.start)
    );
    const [localEndDate, setLocalEndDate] = useState<string>(
        formatLocalDate(store.dateRange.end)
    );

    useEffect(() => {
        if (isOpen) {
            setLocalStatusFilter(store.statusFilter || '');
            setLocalEdiTypeFilter(store.ediTypeFilter || '');
            setLocalFileNameFilter(store.fileNameFilter || '');
            setLocalClaimNumberFilter(store.claimNumberFilter || '');
            setLocalStartDate(formatLocalDate(store.dateRange.start));
            setLocalEndDate(formatLocalDate(store.dateRange.end));
        }
    }, [isOpen, store.statusFilter, store.ediTypeFilter, store.fileNameFilter, store.claimNumberFilter, store.dateRange]);

    const handleClose = () => {
        onClose();
    };

    const handleApplyFilters = () => {
        store.setStatusFilter(localStatusFilter || null);
        store.setEdiTypeFilter(localEdiTypeFilter || null);
        store.setFileNameFilter(localFileNameFilter || null);
        store.setClaimNumberFilter(localClaimNumberFilter || null);
        store.applyCustomDateRange(localStartDate, localEndDate);

        store.loadDailyStats();
        store.loadFileList();
        onClose();
    };

    const handleClearFilters = () => {
        store.setStatusFilter(null);
        store.setEdiTypeFilter(null);
        store.setFileNameFilter(null);
        store.setClaimNumberFilter(null);
        store.clearDateRangeFilter();

        setLocalStatusFilter('');
        setLocalEdiTypeFilter('');
        setLocalFileNameFilter('');
        setLocalClaimNumberFilter('');
        setLocalStartDate(formatLocalDate(store.dateRange.start));
        setLocalEndDate(formatLocalDate(store.dateRange.end));

        store.loadDailyStats();
        store.loadFileList();
        onClose();
    };

    const handleStatusChange = (e: SelectChangeEvent<unknown>) => {
        setLocalStatusFilter(e.target.value as string);
    };

    const handleEdiTypeChange = (e: SelectChangeEvent<unknown>) => {
        setLocalEdiTypeFilter(e.target.value as string);
    };

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    width: 400,
                    maxWidth: '100%',
                }
            }}
        >
            {/* Header */}
            <Row 
                sx={{ 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    height: 'auto',
                }}
            >
                <Typography variant="h6" fontWeight={600}>
                    All Filters
                </Typography>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </Row>

            {/* Filter Content */}
            <Box sx={{ flex: 1, overflow: 'auto', padding: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <CoreInput
                    label="Start Date"
                    type="date"
                    value={localStartDate}
                    onChange={(e) => setLocalStartDate(e.target.value)}
                />

                <CoreInput
                    label="End Date"
                    type="date"
                    value={localEndDate}
                    onChange={(e) => setLocalEndDate(e.target.value)}
                />

                <CoreSelect
                    label="Status"
                    placeholder="All"
                    options={STATUS_OPTIONS}
                    value={localStatusFilter}
                    onChange={handleStatusChange}
                />

                <CoreSelect
                    label="EDI Type"
                    placeholder="All"
                    options={EDI_TYPE_OPTIONS}
                    value={localEdiTypeFilter}
                    onChange={handleEdiTypeChange}
                />

                <CoreInput
                    label="File name"
                    placeholder="e.g. HOSP1801861190033126.837CLN"
                    value={localFileNameFilter}
                    onChange={(e) => setLocalFileNameFilter(e.target.value)}
                />

                <CoreInput
                    label="Claim number"
                    placeholder="Files containing this claim"
                    value={localClaimNumberFilter}
                    onChange={(e) => setLocalClaimNumberFilter(e.target.value)}
                />
            </Box>

            <Divider />

            {/* Footer Actions */}
            <Row sx={{ padding: 2, gap: 2, justifyContent: 'flex-end', height: 'auto', }}>
                <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    sx={{ textTransform: 'none' }}
                >
                    Clear All
                </Button>
                <Button
                    variant="contained"
                    onClick={handleApplyFilters}
                    sx={{ textTransform: 'none' }}
                >
                    Apply Filters
                </Button>
            </Row>
        </Drawer>
    );
};

export default observer(AllFiltersDrawer);

